'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import * as React from 'react'
import { cn } from '@/lib/utils'
import { useBreakpointValue } from '@/hooks/use-mobile'

type LeagueCard = {
  id: string
  label: string
  width: number
  background: string
  textColor: string
}

type LeagueRow = {
  id: string
  cards: LeagueCard[]
  direction?: 'left' | 'right'
  durationSeconds?: number
}

const CARD_SHADOW =
  '-10px -10px 30px var(--neu-shadow-light), 10px 10px 30px var(--neu-shadow-dark)'

const LEAGUE_ROWS: LeagueRow[] = [
  {
    id: 'top',
    direction: 'left',
    durationSeconds: 32,
    cards: [
      {
        id: 'england-main',
        label: 'ENGLISH LEAGUE',
        width: 560,
        background: '#FFFFFF',
        textColor: '#3D195B'
      },
      {
        id: 'spain-main',
        label: 'SPANISH LEAGUE',
        width: 560,
        background: '#FFFFFF',
        textColor: '#FF4B44'
      },
      {
        id: 'germany-main',
        label: 'GERMAN LEAGUE',
        width: 560,
        background: '#D10214',
        textColor: '#FFFFFF'
      },
      {
        id: 'italy-main',
        label: 'ITALIAN LEAGUE',
        width: 560,
        background: '#0471FA',
        textColor: '#1A1858'
      },
      {
        id: 'france-main',
        label: 'FRENCH LEAGUE',
        width: 560,
        background: '#085EFF',
        textColor: '#FFFFFF'
      }
    ]
  },
  {
    id: 'bottom',
    direction: 'right',
    durationSeconds: 36,
    cards: [
      {
        id: 'italy-alt',
        label: 'ITALIAN LEAGUE',
        width: 560,
        background: '#0471FA',
        textColor: '#1A1858'
      },
      {
        id: 'france-alt',
        label: 'FRENCH LEAGUE',
        width: 560,
        background: '#085EFF',
        textColor: '#FFFFFF'
      },
      {
        id: 'england-alt',
        label: 'ENGLISH LEAGUE',
        width: 560,
        background: '#FFFFFF',
        textColor: '#3D195B'
      },
      {
        id: 'spain-alt',
        label: 'SPANISH LEAGUE',
        width: 560,
        background: '#FFFFFF',
        textColor: '#FF4B44'
      },
      {
        id: 'germany-alt',
        label: 'GERMAN LEAGUE',
        width: 560,
        background: '#D10214',
        textColor: '#FFFFFF'
      }
    ]
  }
]

const SCALE_TIERS = { base: 0.45, sm: 0.55, md: 0.65, lg: 1.0 } as const

export default function LeagueBadgesCarousel() {
  const scale = useBreakpointValue(SCALE_TIERS)

  return (
    <section className="relative py-8 md:py-16 overflow-hidden">
      <div className="relative w-screen left-1/2 -translate-x-1/2 space-y-6 md:space-y-12 px-0">
        {LEAGUE_ROWS.map((row) => (
          // Seamless marquee: the row is a flex viewport holding TWO identical
          // card groups separated by --gap. Each group animates by exactly its
          // own width + one gap (see `neu-marquee-*` in globals.css), so group 2
          // lands precisely where group 1 began. Pure CSS — no JS measuring and
          // no remount, so there is nothing to drift or snap.
          <div
            key={row.id}
            className={cn(
              'relative flex overflow-hidden',
              '[--gap:0.75rem] sm:[--gap:1.5rem] md:[--gap:2.5rem]',
              'gap-[var(--gap)]'
            )}
          >
            {[0, 1].map((copy) => (
              <div
                key={copy}
                aria-hidden={copy === 1}
                className="flex w-max shrink-0 flex-nowrap items-center gap-[var(--gap)] will-change-transform"
                style={{
                  animationName:
                    row.direction === 'right' ? 'neu-marquee-right' : 'neu-marquee-left',
                  animationDuration: `${row.durationSeconds ?? 32}s`,
                  animationTimingFunction: 'linear',
                  animationIterationCount: 'infinite'
                }}
              >
                {row.cards.map((card) => (
                  <LeagueCardPill key={card.id} card={card} scale={scale} />
                ))}
              </div>
            ))}

            {/* Soft fades on edges to mimic overflow into darkness */}
            <div
              className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 md:w-24"
              style={{
                background: 'linear-gradient(to right, var(--landing-surface), transparent)'
              }}
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 md:w-24"
              style={{
                background: 'linear-gradient(to left, var(--landing-surface), transparent)'
              }}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

function LeagueCardPill({
  card,
  scale,
  ...props
}: {
  card: LeagueCard
  scale: number
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex-shrink-0',
        'rounded-xl md:rounded-2xl px-3 py-2 sm:px-6 sm:py-4 md:px-10 md:py-5',
        'uppercase font-montserrat font-bold tracking-[0.15em]',
        'text-center text-sm sm:text-2xl md:text-[2.5rem] leading-tight flex items-center justify-center select-none'
      )}
      style={{
        width: card.width * scale,
        background: card.background,
        color: card.textColor,
        boxShadow: CARD_SHADOW
      }}
      {...props}
    >
      {card.label}
    </div>
  )
}
